import React, { useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import GradedCanvas from './GradedCanvas';
import GradePanel from './GradePanel';
import Scopes from './Scopes';
import SplitPreview from './SplitPreview';
import CropBox from './CropBox';
import KeyframeStoryboard from './KeyframeStoryboard';
import { useGradePresets, addGradePreset, deleteGradePreset } from '../data/firestore';
import {
  DEFAULT_GRADE,
  clone,
  gradeSignature,
  composeGrade,
  activeLayers,
  autoGrade,
  gradeAtTime,
  applyLook,
  neutralLook,
  stampPreset,
  PRESETS,
} from '../utils/grade';
import {
  CROP_ASPECTS,
  DEFAULT_CROP_RECT,
  rectForAR,
  rectScale,
  rectAtScale,
  normalizeCrop,
  zoomFromScale,
  scaleFromZoom,
  sortReframeKeys,
  reframeAtTime,
  reframeIsActive,
} from '../utils/crop';
import { fmtTime } from '../utils/movie';
import { useNow, renderJobLabel, renderJobActive } from '../utils/eta';
import { fmtUSD } from '../utils/cost';

const mkId = () => `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// Side-by-side, non-destructive color/light studio for a SINGLE clip (asset).
// Two modes:
//   • Single look  — one grade for the whole clip (saved to asset.grade).
//   • Keyframes    — define a segment of interest, seed N evenly-spaced
//     keyframes, grade each on its own frame; the look interpolates across the
//     clip live (blendGrade) and bakes to a graded MP4 server-side.
//
//   url, type            resolved playback url (https) + 'hls' | 'mp4'
//   grade, onChange      single-look grade (controlled by parent; parent debounces)
//   getFreshSrc          optional async () => url to re-sign an expired MP4 link
//   keyframes, segment   keyframe state (controlled by parent)
//   onKeyframesChange    ({ keyframes, segment }) => void — parent persists
//   onRequestRender      () => void — queue the server-side bake
//   renderJob, clipResult  live render descriptor + finished MP4 ({ url, ... })
//   crop, onCropChange   non-destructive reframe {x,y,w,h,rotate?,flipH?,flipV?}
//     (or null) — a static "zoom" baked into the clip render; parent persists.
//   reframeKeys, onReframeChange  animated zoom [{id,t,rect}] (push-in/pull-out)
//     interpolated per frame; baked by the worker; parent persists.
export default function ClipColorStudio({
  url,
  type,
  grade,
  onChange,
  getFreshSrc,
  keyframes = [],
  segment = null,
  onKeyframesChange,
  onRequestRender,
  renderJob = null,
  clipResult = null,
  crop = null,
  onCropChange,
  reframeKeys = [],
  onReframeChange,
}) {
  const { docs: presets } = useGradePresets();

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [videoEl, setVideoEl] = useState(null);
  const [stats, setStats] = useState(null);
  const [origStats, setOrigStats] = useState(null);
  const [playing, setPlaying] = useState(true);
  const [err, setErr] = useState('');
  const [layers, setLayers] = useState({ light: true, color: true, effects: true });
  const [viewMode, setViewMode] = useState('split'); // 'split' | 'single'
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);

  const [mode, setMode] = useState('single'); // 'single' | 'keyframes'
  const [selectedKfId, setSelectedKfId] = useState(null);
  const [seedN, setSeedN] = useState(5);

  const [cropMode, setCropMode] = useState(false); // reframe / zoom overlay open
  const [aspectId, setAspectId] = useState('source');
  const [cropSubmode, setCropSubmode] = useState('static'); // 'static' | 'animated'
  const [selectedReframeId, setSelectedReframeId] = useState(null);
  const [boardView, setBoardView] = useState(false); // storyboard (big frame grid)

  // Final-bake output control. Editing stays on the fast proxy; the bake can
  // re-decode the RED RAW at native res for max-resolution delivery.
  const isRed = type === 'hls';
  const [outRes, setOutRes] = useState('max'); // max | uhd | 1080 | proxy
  const [outQuality, setOutQuality] = useState('high'); // standard | high | max

  const rjNow = useNow(renderJobActive(renderJob));

  // Attach the source (hls.js for .m3u8, native otherwise).
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return undefined;
    setErr('');
    const isHls = type === 'hls' || url.endsWith('.m3u8');
    if (isHls && Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data?.fatal) setErr('Could not load this clip for grading.');
      });
      return () => { hls.destroy(); hlsRef.current = null; };
    }
    video.src = url;
    return undefined;
  }, [url, type]);

  // Keyframe quality: while PARKED on a frame (paused) — i.e. grading or
  // reframing/zooming a keyframe — pin the top (UHD) HLS variant so the surface
  // is crisp and accurate. During playback let it adapt for smooth scrubbing.
  // (Editing on a lighter variant is fine; the final bake re-decodes the RAW.)
  useEffect(() => {
    const hls = hlsRef.current;
    if (!hls) return;
    try {
      if (!playing && hls.levels && hls.levels.length > 1) hls.currentLevel = hls.levels.length - 1;
      else hls.currentLevel = -1;
    } catch { /* noop */ }
  }, [playing, videoEl, cropMode, selectedKfId, selectedReframeId]);

  // Recover an expired presigned MP4 link without losing position.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !getFreshSrc) return undefined;
    const isHls = type === 'hls' || (url && url.endsWith('.m3u8'));
    if (isHls) return undefined;
    let recovering = false;
    const recover = async () => {
      if (recovering) return;
      recovering = true;
      const at = video.currentTime;
      try {
        const fresh = await getFreshSrc();
        if (fresh) {
          const onMeta = () => {
            video.removeEventListener('loadedmetadata', onMeta);
            try { if (at > 0) video.currentTime = at; } catch { /* noop */ }
            video.play().catch(() => {});
          };
          video.addEventListener('loadedmetadata', onMeta);
          video.src = fresh;
          video.load();
        }
      } catch { /* surface native error if re-sign also fails */ } finally {
        setTimeout(() => { recovering = false; }, 5000);
      }
    };
    video.addEventListener('error', recover);
    return () => video.removeEventListener('error', recover);
  }, [url, type, getFreshSrc]);

  // Wire the <video> into the canvases once it can render.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onReady = () => { setVideoEl(v); setDur(v.duration || 0); };
    v.addEventListener('loadeddata', onReady);
    v.addEventListener('durationchange', () => setDur(v.duration || 0));
    if (v.readyState >= 2) onReady();
    return () => v.removeEventListener('loadeddata', onReady);
  }, [url]);

  // Throttled playhead read for the scrubber.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    let raf;
    let last = 0;
    const loop = (ts) => {
      if (ts - last > 90) { last = ts; setT(v.currentTime || 0); }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [videoEl]);

  // Keep a valid keyframe selected while in keyframe mode.
  useEffect(() => {
    if (mode !== 'keyframes') return;
    if (selectedKfId && keyframes.some((k) => k.id === selectedKfId)) return;
    setSelectedKfId(keyframes[0]?.id || null);
  }, [mode, keyframes, selectedKfId]);

  // Keep a valid reframe key selected while editing animated zoom.
  useEffect(() => {
    if (!(cropMode && cropSubmode === 'animated')) return;
    if (selectedReframeId && reframeKeys.some((k) => k.id === selectedReframeId)) return;
    setSelectedReframeId(sortReframeKeys(reframeKeys)[0]?.id || null);
  }, [cropMode, cropSubmode, reframeKeys, selectedReframeId]);

  const sortedKfs = useMemo(
    () => [...keyframes].filter((k) => k && k.grade).sort((a, b) => (a.t || 0) - (b.t || 0)),
    [keyframes]
  );
  const selectedKf = useMemo(() => sortedKfs.find((k) => k.id === selectedKfId) || null, [sortedKfs, selectedKfId]);
  const keyed = mode === 'keyframes' && sortedKfs.length > 0;

  // What the right (graded) canvas shows — interpolated look at the playhead in
  // keyframe mode, else the single grade. Composed through the layer toggles.
  const previewGrade = useMemo(
    () => (keyed ? gradeAtTime(sortedKfs, t, grade) : grade),
    [keyed, sortedKfs, t, grade]
  );
  const compositeGrade = useMemo(() => composeGrade(previewGrade, layers), [previewGrade, layers]);

  // What the GradePanel / Auto / Styles edit. In keyframe mode that's the
  // selected keyframe; otherwise the single grade.
  const activeGrade = mode === 'keyframes' ? (selectedKf?.grade || previewGrade) : grade;
  const live = useMemo(() => activeLayers(activeGrade), [activeGrade]);

  const commitKeyframes = (nextKfs, nextSeg = segment) => {
    onKeyframesChange?.({ keyframes: nextKfs, segment: nextSeg });
  };

  // Route a grade edit to the right target (selected keyframe vs single grade).
  const setActiveGrade = (next) => {
    if (mode === 'keyframes') {
      if (!selectedKfId) return;
      commitKeyframes(keyframes.map((k) => (k.id === selectedKfId ? { ...k, grade: next } : k)));
    } else {
      onChange(next);
    }
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const seek = (sec) => {
    const v = videoRef.current;
    if (!v) return;
    try { v.currentTime = Math.max(0, sec); } catch { /* noop */ }
    setT(sec);
  };

  const segIn = segment && Number.isFinite(segment.inSec) ? segment.inSec : 0;
  const segOut = segment && Number.isFinite(segment.outSec) ? segment.outSec : (dur || 0);
  const pct = (sec) => (dur > 0 ? Math.max(0, Math.min(100, (sec / dur) * 100)) : 0);

  const seedKeyframes = () => {
    const lo = Math.min(segIn, segOut);
    const hi = Math.max(segIn, segOut || dur || 0);
    const span = hi - lo > 0.05 ? hi - lo : dur || 0;
    const N = Math.max(2, Math.min(20, Math.round(seedN) || 5));
    const base = selectedKf?.grade || grade;
    const kfs = Array.from({ length: N }, (_, i) => ({
      id: mkId(),
      t: lo + (span * i) / (N - 1),
      grade: clone(base || DEFAULT_GRADE),
    }));
    commitKeyframes(kfs, { inSec: lo, outSec: lo + span });
    setSelectedKfId(kfs[0].id);
    setMode('keyframes');
  };

  const addKeyframeAtPlayhead = () => {
    const kf = { id: mkId(), t, grade: clone(previewGrade || DEFAULT_GRADE) };
    commitKeyframes([...keyframes, kf].sort((a, b) => a.t - b.t));
    setSelectedKfId(kf.id);
  };

  const deleteSelected = () => {
    if (!selectedKfId) return;
    const next = keyframes.filter((k) => k.id !== selectedKfId);
    commitKeyframes(next);
    setSelectedKfId(next[0]?.id || null);
  };

  const selectKf = (kf) => { setSelectedKfId(kf.id); seek(kf.t); };

  // ---- non-destructive reframe / zoom (static crop baked into the render) ----
  const srcAR = videoEl && videoEl.videoWidth ? videoEl.videoWidth / videoEl.videoHeight : 0;
  const cropAspect = CROP_ASPECTS.find((a) => a.id === aspectId) || CROP_ASPECTS[0];
  const cropAspectR = cropAspect.id === 'source' ? srcAR : cropAspect.r;
  const lockRatio = cropAspectR && srcAR ? cropAspectR / srcAR : null;
  const geo = { rotate: crop?.rotate || 0, flipH: !!crop?.flipH, flipV: !!crop?.flipV };
  const withGeo = (rect) => (rect ? { ...rect, rotate: geo.rotate, flipH: geo.flipH, flipV: geo.flipV } : rect);
  const commitCrop = (rect) => onCropChange?.(normalizeCrop(rect));
  const onCropBox = (next) => commitCrop(withGeo(next));
  const setGeo = (partial) => {
    const base = crop && Number(crop.w) > 0 ? crop : { x: 0, y: 0, w: 1, h: 1 };
    commitCrop({ ...base, rotate: geo.rotate, flipH: geo.flipH, flipV: geo.flipV, ...partial });
  };
  const chooseAspect = (id) => {
    setAspectId(id);
    const targetAR = id === 'source' ? srcAR : (CROP_ASPECTS.find((x) => x.id === id) || {}).r;
    const rect = !targetAR
      ? (crop && crop.w > 0 ? crop : { ...DEFAULT_CROP_RECT })
      : (rectForAR(targetAR, 0.85, srcAR) || { ...DEFAULT_CROP_RECT });
    commitCrop(withGeo(rect));
  };
  const removeCrop = () => { setAspectId('source'); commitCrop(null); };

  // ---- animated reframe ("keyframed zoom"): push-in / pull-out over time -----
  const sortedRk = useMemo(() => sortReframeKeys(reframeKeys), [reframeKeys]);
  const animated = cropSubmode === 'animated' && sortedRk.length >= 1;
  const rk0 = sortedRk[0] || null;
  const selectedRk = useMemo(
    () => sortedRk.find((k) => k.id === selectedReframeId) || sortedRk[0] || null,
    [sortedRk, selectedReframeId]
  );
  // All keys share key[0]'s normalized proportion so the render output is a
  // constant size; lock the crop box to it while editing.
  const rkLock = rk0 ? rk0.rect.w / rk0.rect.h : null;
  const commitReframe = (nextKeys) => onReframeChange?.(nextKeys);
  const rectOnly = (r) => ({ x: r.x, y: r.y, w: r.w, h: r.h });
  const seedReframe = () => {
    const start = crop && crop.w > 0 ? rectOnly(crop) : (rectForAR(srcAR, 1, srcAR) || { x: 0, y: 0, w: 1, h: 1 });
    const end = rectAtScale(start, 0.55); // ~1.8× push-in, same center
    const lo = segment && Number.isFinite(segment.inSec) ? segment.inSec : 0;
    const hi = segment && Number.isFinite(segment.outSec) ? segment.outSec : (dur || lo + 2);
    const k0 = { id: mkId(), t: lo, rect: start };
    const k1 = { id: mkId(), t: hi > lo + 0.05 ? hi : lo + Math.max(1, (dur || 2) - lo), rect: end };
    commitReframe([k0, k1]);
    setSelectedReframeId(k0.id);
    setCropSubmode('animated');
  };
  const addReframeKey = () => {
    const rect = reframeAtTime(sortedRk, t) || (crop && crop.w > 0 ? rectOnly(crop) : { x: 0, y: 0, w: 1, h: 1 });
    const k = { id: mkId(), t, rect: rectOnly(rect) };
    commitReframe([...reframeKeys, k]);
    setSelectedReframeId(k.id);
  };
  const deleteReframeKey = () => {
    if (!selectedRk) return;
    const next = reframeKeys.filter((k) => k.id !== selectedRk.id);
    commitReframe(next);
    const ns = sortReframeKeys(next);
    setSelectedReframeId(ns[0]?.id || null);
  };
  const setSelectedRect = (rect) => {
    if (!selectedRk) return;
    commitReframe(reframeKeys.map((k) => (k.id === selectedRk.id ? { ...k, rect: rectOnly(rect) } : k)));
  };
  const selectReframeKey = (k) => { setSelectedReframeId(k.id); seek(k.t); };
  const clearReframe = () => { commitReframe([]); setSelectedReframeId(null); };

  // Zoom slider + crop box target the right thing for the active submode.
  const zoomTargetRect = animated ? (selectedRk?.rect || null) : crop;
  const cropZoom = zoomFromScale(rectScale(zoomTargetRect));
  const applyCropZoom = (z) => {
    if (animated) setSelectedRect(rectAtScale(selectedRk?.rect || { x: 0, y: 0, w: 1, h: 1 }, scaleFromZoom(z)));
    else commitCrop(normalizeCrop(withGeo(rectAtScale(crop, scaleFromZoom(z)))));
  };
  // Live crop the (non-edit) preview samples: interpolated window in animated
  // mode (so scrubbing plays the push-in), else the static rect. Geometry rides
  // along uniformly.
  const cropGetter = (src) => {
    if (animated) {
      const r = reframeAtTime(sortedRk, (src && src.currentTime) || 0);
      return r ? { ...r, rotate: geo.rotate, flipH: geo.flipH, flipV: geo.flipV } : null;
    }
    return crop;
  };
  const cropEditGetter = () => ({ x: 0, y: 0, w: 1, h: 1, rotate: geo.rotate, flipH: geo.flipH, flipV: geo.flipV });
  const hasReframe = !!normalizeCrop(crop) || reframeIsActive(reframeKeys);
  const singleActive = gradeSignature(grade) !== gradeSignature(DEFAULT_GRADE);
  const canRender = sortedKfs.length > 0 || hasReframe || singleActive;

  // Storyboard ("Frames"): a big contact sheet of the active keys across the
  // clip. Reframe board when editing an animated zoom; grade board when editing
  // grade keyframes. Each card resolves the look + framing at its key time.
  const board = useMemo(() => {
    if (cropMode && cropSubmode === 'animated' && sortedRk.length) {
      return {
        mode: 'reframe',
        items: sortedRk.map((k) => ({ id: k.id, t: k.t, grade: gradeAtTime(sortedKfs, k.t, grade), rect: k.rect })),
      };
    }
    if (mode === 'keyframes' && sortedKfs.length) {
      return {
        mode: 'grade',
        items: sortedKfs.map((k) => ({ id: k.id, t: k.t, grade: k.grade, rect: reframeAtTime(sortedRk, k.t) || crop || null })),
      };
    }
    return null;
  }, [cropMode, cropSubmode, sortedRk, sortedKfs, mode, grade, crop]);
  const canBoard = !!board;
  const showBoard = boardView && canBoard;
  const boardSelectedId = board?.mode === 'reframe' ? (selectedRk?.id || null) : selectedKfId;
  const onBoardSelect = (id) => {
    if (board?.mode === 'reframe') setSelectedReframeId(id);
    else setSelectedKfId(id);
  };
  const onBoardRect = (id, rect) => {
    commitReframe(reframeKeys.map((k) => (k.id === id ? { ...k, rect: rectOnly(rect) } : k)));
    setSelectedReframeId(id);
  };

  // cropStyle must stay shrink-wrapped (width-driven) — the CropBox overlay is
  // glued to the canvas box and assumes box == displayed frame.
  const cropStyle = { width: '100%', height: 'auto', display: 'block' };
  // The compare panes fill their pane box and letterbox via object-fit:contain,
  // so the frame keeps its true aspect ratio in any responsive layout (never
  // squashed/cropped by a short stage). Same style on BOTH panes so raw and
  // graded always render at the same scale.
  const paneFitStyle = { width: '100%', height: '100%', objectFit: 'contain', display: 'block' };
  const leftStyle = paneFitStyle;
  const rightStyle = paneFitStyle;

  const layerRows = [
    { key: 'light', label: 'Light', hint: 'Exposure, contrast, tone', live: live.light },
    { key: 'color', label: 'Color', hint: 'WB, vibrance, color mixer, grading', live: live.color },
    { key: 'effects', label: 'Effects', hint: 'Clarity, vignette, grain, sharpen', live: live.effects },
  ];

  const leftPane = (
    videoEl ? (
      <GradedCanvas source={videoEl} grade={DEFAULT_GRADE} getCrop={cropGetter} onStats={setOrigStats} onError={setErr} maxWidth={1600} style={leftStyle} />
    ) : (
      <div className="cs-studio-ph"><span className="cs-spinner" /><strong>Original</strong><small>loading the clip…</small></div>
    )
  );
  const rightPane = (
    videoEl ? (
      <GradedCanvas source={videoEl} grade={compositeGrade} getCrop={cropGetter} onStats={setStats} onError={setErr} maxWidth={1600} style={rightStyle} />
    ) : (
      <div className="cs-studio-ph"><span className="cs-spinner" /><strong>Graded</strong><small>your look renders here</small></div>
    )
  );

  const outputSpec = useMemo(() => {
    switch (outRes) {
      case 'proxy': return { source: 'proxy', heightTarget: 0, quality: outQuality };
      case 'uhd': return { source: 'master', heightTarget: 2160, quality: outQuality };
      case '1080': return { source: 'master', heightTarget: 1080, quality: outQuality };
      default: return { source: 'master', heightTarget: 0, quality: outQuality };
    }
  }, [outRes, outQuality]);

  const rj = renderJob;
  const rjBusy = renderJobActive(rj);
  const rjCostUsd = rj?.costUsd != null ? rj.costUsd : rj?.costEstUsd;

  return (
    <div className="cs-studio">
      <div className="cs-studio-main">
        <video
          ref={videoRef}
          crossOrigin="anonymous"
          muted
          loop
          autoPlay
          playsInline
          style={{ display: 'none' }}
        />
        <div className={`cs-studio-stage${(viewMode === 'single' || cropMode) ? ' single' : ''}`}>
          {err ? (
            <div className="cs-empty">{err}</div>
          ) : showBoard ? (
            <div className="cs-studio-single" style={{ display: 'block' }}>
              <div className="cs-split-label" style={{ background: 'var(--surf)' }}>
                {board.mode === 'reframe'
                  ? `Frames · reframe — drag the box on the selected card · ${board.items.length} keys`
                  : `Frames · grade — click a card to edit that keyframe · ${board.items.length} keys`}
              </div>
              <KeyframeStoryboard
                videoRef={videoRef}
                videoEl={videoEl}
                mode={board.mode}
                items={board.items}
                selectedId={boardSelectedId}
                onSelect={onBoardSelect}
                onRectChange={onBoardRect}
                layers={layers}
                geo={geo}
                lockRatio={rkLock}
              />
            </div>
          ) : cropMode ? (
            <div className="cs-studio-single">
              <div className="cs-split-label" style={{ background: 'var(--surf)' }}>
                {animated
                  ? `Reframe ◆ key ${Math.max(1, sortedRk.findIndex((k) => k.id === selectedRk?.id) + 1)}/${sortedRk.length} @ ${fmtTime(selectedRk?.t || 0)}`
                  : `Reframe / zoom · ${fmtTime(t)}`}
              </div>
              <div className="cs-studio-cropwrap">
                {videoEl ? (
                  <GradedCanvas source={videoEl} grade={compositeGrade} getCrop={cropEditGetter} onError={setErr} maxWidth={1600} style={cropStyle} />
                ) : (
                  <div className="cs-studio-ph"><span className="cs-spinner" /><strong>Reframe</strong><small>loading the clip…</small></div>
                )}
                {cropSubmode === 'animated' ? (
                  selectedRk ? (
                    <CropBox rect={selectedRk.rect} lockRatio={rkLock} onChange={setSelectedRect} />
                  ) : null
                ) : (
                  <CropBox rect={crop || DEFAULT_CROP_RECT} lockRatio={lockRatio} onChange={onCropBox} />
                )}
              </div>
            </div>
          ) : viewMode === 'single' ? (
            <div className="cs-studio-single">
              <div className="cs-split-label" style={{ background: 'var(--surf)' }}>
                {keyed ? `Graded · ${fmtTime(t)}` : 'Graded'}
              </div>
              {rightPane}
            </div>
          ) : (
            <SplitPreview
              panes={[
                { label: 'Original (raw)', color: '#888' },
                { label: keyed ? `Graded · ${fmtTime(t)}` : 'Graded look', color: 'var(--surf)' },
              ]}
            >
              {leftPane}
              {rightPane}
            </SplitPreview>
          )}
        </div>

        {cropMode && (
          <div className="cs-crop-toolbar">
            <div className="cs-segment" title="One fixed reframe vs an animated push-in / pull-out">
              <button className={cropSubmode === 'static' ? 'on' : ''} onClick={() => setCropSubmode('static')}>Static</button>
              <button className={cropSubmode === 'animated' ? 'on' : ''} onClick={() => setCropSubmode('animated')}>◆ Animated</button>
            </div>
            <span className="cs-crop-sep" />
            {cropSubmode === 'animated' && sortedRk.length < 1 ? (
              <>
                <span className="cs-help">Animate a zoom: seed a wide→tight push-in, then tune each key.</span>
                <button className="cs-btn cs-btn-sm cs-btn-primary" disabled={!videoEl} onClick={seedReframe}>◆ Seed push-in</button>
              </>
            ) : (
              <>
                <span className="cs-crop-grp-label">Zoom{animated ? ' (key)' : ''}</span>
                <input
                  type="range"
                  min={1}
                  max={4}
                  step={0.02}
                  value={cropZoom}
                  onChange={(e) => applyCropZoom(Number(e.target.value))}
                  title={animated
                    ? 'Zoom for the selected keyframe. RED proxy is UHD — moderate zoom stays sharp; heavy zoom upscales.'
                    : "Zoom / tighten the framing. RED's oversampled RAW keeps moderate zoom sharp; drag the box to recompose."}
                  style={{ width: 120 }}
                  disabled={!videoEl || (animated && !selectedRk)}
                />
                <span className="cs-crop-grp-label" style={{ minWidth: 38 }}>{cropZoom.toFixed(2)}×</span>
                {cropZoom > 1.005 && (
                  <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => applyCropZoom(1)} title="Reset zoom to the widest framing">⟲</button>
                )}
                <span className="cs-crop-sep" />
                <span className="cs-crop-grp-label">Straighten</span>
                <input
                  type="range"
                  min={-45}
                  max={45}
                  step={0.1}
                  value={geo.rotate}
                  onChange={(e) => setGeo({ rotate: Number(e.target.value) })}
                  title="Rotate to level the horizon (−45°…45°) — applies across the whole clip"
                  style={{ width: 100 }}
                  disabled={!videoEl}
                />
                <span className="cs-crop-grp-label" style={{ minWidth: 38 }}>{geo.rotate ? `${geo.rotate.toFixed(1)}°` : '0°'}</span>
                {geo.rotate !== 0 && (
                  <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => setGeo({ rotate: 0 })} title="Reset straighten">⟲</button>
                )}
                <span className="cs-crop-sep" />
                <button className={`cs-btn cs-btn-sm ${geo.flipH ? 'cs-btn-primary' : 'cs-btn-ghost'}`} onClick={() => setGeo({ flipH: !geo.flipH })} title="Flip horizontal">⇋ H</button>
                <button className={`cs-btn cs-btn-sm ${geo.flipV ? 'cs-btn-primary' : 'cs-btn-ghost'}`} onClick={() => setGeo({ flipV: !geo.flipV })} title="Flip vertical">⇅ V</button>
                <span className="cs-crop-sep" />
                {animated ? (
                  <>
                    <button className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!dur} onClick={addReframeKey} title="Add a reframe keyframe at the playhead">+ Key</button>
                    <button className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!selectedRk || sortedRk.length <= 1} onClick={deleteReframeKey} title="Delete the selected reframe keyframe">🗑 Key</button>
                    <button className="cs-btn cs-btn-sm cs-btn-ghost cs-crop-remove" onClick={clearReframe} title="Remove the animated zoom entirely">Clear</button>
                  </>
                ) : (
                  <>
                    <span className="cs-crop-grp-label">Lock ratio</span>
                    {CROP_ASPECTS.map((a) => {
                      const on = aspectId === a.id && !!crop;
                      return (
                        <button
                          key={a.id}
                          className={`cs-btn cs-btn-sm ${on ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                          onClick={() => chooseAspect(a.id)}
                          title={a.id === 'source' ? 'Lock to the original frame’s aspect' : a.id === 'free' ? 'Any shape — drag corners/edges' : `Lock to ${a.label}`}
                        >
                          {a.label}
                        </button>
                      );
                    })}
                    <span className="cs-crop-sep" />
                    <button className="cs-btn cs-btn-sm cs-btn-ghost cs-crop-remove" disabled={!crop} onClick={removeCrop} title="Delete the reframe — use the whole frame">🗑 Remove</button>
                  </>
                )}
              </>
            )}
            <button className="cs-btn cs-btn-sm cs-btn-primary" style={{ marginLeft: 'auto' }} onClick={() => setCropMode(false)}>Done</button>
          </div>
        )}

        <div className="cs-studio-bar">
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={togglePlay} disabled={!videoEl}>
            {playing ? '❚❚ Pause' : '► Play'}
          </button>
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={() => setViewMode((v) => (v === 'split' ? 'single' : 'split'))}
            disabled={cropMode}
          >
            {viewMode === 'split' ? '⤢ Single view' : '⊞ Split view'}
          </button>
          <button
            className={`cs-btn cs-btn-sm ${cropMode ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
            onClick={() => setCropMode((v) => !v)}
            disabled={!videoEl}
            title="Reframe / zoom — a non-destructive crop baked into the rendered clip"
          >
            {cropMode ? '✓ Done framing' : `✂ Crop / Zoom${hasReframe ? ' •' : ''}`}
          </button>
          <button
            className={`cs-btn cs-btn-sm ${showBoard ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
            onClick={() => setBoardView((v) => !v)}
            disabled={!videoEl || !canBoard}
            title={canBoard
              ? 'Frames — a big contact sheet of every keyframe across the clip, for grading / reframing throughout the duration'
              : 'Add grade keyframes (◆ Keyframes) or an animated reframe to use the big frame board'}
          >
            {showBoard ? '✓ Frames' : '▦ Frames'}
          </button>
          <div className="cs-segment" style={{ marginLeft: 4 }} title="Single look vs time-varying keyframes">
            <button className={mode === 'single' ? 'on' : ''} onClick={() => setMode('single')}>Single look</button>
            <button className={mode === 'keyframes' ? 'on' : ''} onClick={() => setMode('keyframes')}>◆ Keyframes</button>
          </div>
          <div className="cs-layerchips">
            {layerRows.map((r) => (
              <button
                key={r.key}
                className={`cs-layerchip ${layers[r.key] ? 'on' : ''}`}
                title={`Toggle ${r.label} on the graded view`}
                onClick={() => setLayers((p) => ({ ...p, [r.key]: !p[r.key] }))}
              >
                <span className="cs-layerchip-box">{layers[r.key] ? '✓' : ''}</span>{r.label}
              </button>
            ))}
          </div>
          <span className="cs-help">
            {keyed ? 'Right shows the interpolated look at the playhead.' : 'Left = raw source · right builds up your enabled layers live.'}
          </span>
        </div>

        <div className="cs-clipscrub">
          <input
            className="cs-slider"
            type="range"
            min="0"
            max={dur || 0}
            step="0.05"
            value={Math.min(t, dur || 0)}
            onChange={(e) => seek(Number(e.target.value))}
            disabled={!dur}
          />
          <span className="cs-badge">{fmtTime(t)} / {fmtTime(dur)}</span>
        </div>

        {cropMode && cropSubmode === 'animated' && sortedRk.length > 0 && (
          <div className="cs-kf-lane" title="Reframe keyframes (◆) — click one to select, scrub to preview the zoom">
            <div className="cs-kf-play" style={{ left: `${pct(t)}%` }} />
            {sortedRk.map((k) => (
              <button
                key={k.id}
                className={`cs-kf-dot ${k.id === selectedRk?.id ? 'sel' : ''}`}
                style={{ left: `${pct(k.t)}%` }}
                title={`Reframe key @ ${fmtTime(k.t)} · ${zoomFromScale(rectScale(k.rect)).toFixed(2)}× — click to edit`}
                onClick={() => selectReframeKey(k)}
              />
            ))}
          </div>
        )}

        {mode === 'keyframes' && !cropMode && (
          <div className="cs-kf-lane" title="Keyframes (◆) along the clip — click one to edit it">
            {dur > 0 && (
              <div
                className="cs-kf-seg"
                style={{ left: `${pct(Math.min(segIn, segOut))}%`, width: `${Math.max(0, pct(Math.max(segIn, segOut)) - pct(Math.min(segIn, segOut)))}%` }}
              />
            )}
            <div className="cs-kf-play" style={{ left: `${pct(t)}%` }} />
            {sortedKfs.map((k) => (
              <button
                key={k.id}
                className={`cs-kf-dot ${k.id === selectedKfId ? 'sel' : ''}`}
                style={{ left: `${pct(k.t)}%` }}
                title={`Keyframe @ ${fmtTime(k.t)} — click to edit`}
                onClick={() => selectKf(k)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="cs-studio-side">
        {mode === 'keyframes' && (
          <div className="cs-autobar">
            <div className="cs-autobar-label">Segment of interest</div>
            <div className="cs-autobar-row">
              <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!dur} onClick={() => commitKeyframes(keyframes, { inSec: t, outSec: segOut })}>
                ⟦ Set in ({fmtTime(segIn)})
              </button>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!dur} onClick={() => commitKeyframes(keyframes, { inSec: segIn, outSec: t })}>
                Set out ({fmtTime(segOut)}) ⟧
              </button>
            </div>
            <div className="cs-autobar-row" style={{ alignItems: 'center', marginTop: 6 }}>
              <label className="cs-help" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                Keyframes
                <input
                  type="number"
                  min="2"
                  max="20"
                  value={seedN}
                  onChange={(e) => setSeedN(Number(e.target.value))}
                  style={{ width: 52 }}
                  className="cs-input cs-input-sm"
                />
              </label>
              <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={!dur} onClick={seedKeyframes}>
                ◆ Seed evenly
              </button>
            </div>
            <div className="cs-autobar-row" style={{ marginTop: 6 }}>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!dur} onClick={addKeyframeAtPlayhead}>+ Add at playhead</button>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!selectedKfId} onClick={deleteSelected}>🗑 Delete</button>
            </div>
            <div className="cs-help" style={{ marginTop: 6 }}>
              {sortedKfs.length
                ? `Editing keyframe ${Math.max(1, sortedKfs.findIndex((k) => k.id === selectedKfId) + 1)} of ${sortedKfs.length} @ ${fmtTime(selectedKf?.t || 0)}. Scrub to see the look morph between keys; the panel below edits the selected keyframe.`
                : 'Set the segment, then “Seed evenly” to drop keyframes across it. Grade each one — the look interpolates across the clip.'}
            </div>
          </div>
        )}

        <div className="cs-autobar">
          <div className="cs-autobar-label">Render graded clip</div>
          <div className="cs-autobar-row" style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <label className="cs-help" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              Output
              <select
                className="cs-input cs-input-sm"
                value={outRes}
                onChange={(e) => setOutRes(e.target.value)}
                disabled={rjBusy}
                title={isRed
                  ? 'Max / 4K / 1080 re-decode the RED RAW at that resolution (slower, sharpest). Proxy bakes from the fast ≤4K proxy.'
                  : 'Delivery height. The clip already bakes from its full-resolution original.'}
              >
                {isRed
                  ? <option value="max">Max — native RAW (up to 8K)</option>
                  : <option value="max">Max — full source</option>}
                <option value="uhd">4K UHD (2160p)</option>
                <option value="1080">1080p</option>
                {isRed && <option value="proxy">Proxy — fast (≤4K)</option>}
              </select>
            </label>
            {isRed && outRes !== 'proxy' && (
              <label className="cs-help" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                Quality
                <select
                  className="cs-input cs-input-sm"
                  value={outQuality}
                  onChange={(e) => setOutQuality(e.target.value)}
                  disabled={rjBusy}
                  title="RAW intermediate fidelity (higher = larger, slower)"
                >
                  <option value="standard">Standard</option>
                  <option value="high">High</option>
                  <option value="max">Max</option>
                </select>
              </label>
            )}
          </div>
          <div className="cs-autobar-row" style={{ alignItems: 'center' }}>
            <button
              className="cs-btn cs-btn-primary cs-btn-sm"
              disabled={!canRender || rjBusy}
              onClick={() => onRequestRender?.(outputSpec)}
              title="Bake the grade + reframe into a downloadable MP4 of the whole clip"
            >
              🎬 Render clip
            </button>
            {rj && rjBusy && <span className="cs-help">{renderJobLabel(rj, rjNow)} · ~{fmtUSD(rjCostUsd || 0)}</span>}
          </div>
          {!rjBusy && (
            <div className="cs-help" style={{ marginTop: 4 }}>
              {canRender
                ? `Bakes the ${keyed ? 'keyframed look' : 'look'}${hasReframe ? ' + reframe/zoom' : ''} into the whole clip`
                  + (isRed && outRes !== 'proxy' ? ' — re-decodes the RAW for max-resolution delivery (slower).' : '.')
                : 'Add a look (or grade keyframes) or a reframe/zoom to enable rendering.'}
            </div>
          )}
          {clipResult?.url && clipResult.status !== 'error' && !rjBusy && (
            <div style={{ marginTop: 8 }}>
              <video src={clipResult.url} controls playsInline style={{ width: '100%', borderRadius: 8, display: 'block' }} />
              <a className="cs-btn cs-btn-ghost cs-btn-sm" href={clipResult.url} download style={{ marginTop: 6, display: 'inline-block' }}>
                ⤓ Download graded MP4
              </a>
              <div className="cs-help" style={{ marginTop: 4 }}>
                {clipResult.width && clipResult.height ? `${clipResult.width}×${clipResult.height}` : ''}
                {clipResult.source ? ` · ${clipResult.source === 'master' ? 'native RAW' : clipResult.source === 'proxy' ? 'proxy' : 'full source'}` : ''}
                {clipResult.segments ? ` · ${clipResult.segments} segment${clipResult.segments === 1 ? '' : 's'}` : ''}
                {clipResult.keyframes ? ` · ${clipResult.keyframes} keyframes` : ''}
                {clipResult.reframeKeys ? ` · ${clipResult.reframeKeys} zoom keys` : ''}
              </div>
            </div>
          )}
          {!rjBusy && clipResult?.status === 'error' && (
            <div className="cs-help" style={{ color: '#ff9b9b' }}>Render failed: {clipResult.message || 'unknown error'}</div>
          )}
        </div>

        <div className="cs-layerlist">
          {layerRows.map((r) => (
            <div key={r.key} className="cs-layerrow sel">
              <button
                className={`cs-toggle ${layers[r.key] ? 'on' : ''}`}
                title={`Show / hide ${r.label}`}
                onClick={() => setLayers((p) => ({ ...p, [r.key]: !p[r.key] }))}
              >
                <span className="cs-toggle-knob" />
              </button>
              <div className="cs-layername">
                <span className="cs-layertitle">{r.label}{r.live && <i className="cs-livedot" />}</span>
                <span className="cs-layerhint">{r.hint}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="cs-autobar">
          <div className="cs-autobar-row">
            <button
              className="cs-btn cs-btn-primary cs-btn-sm"
              disabled={!origStats || (mode === 'keyframes' && !selectedKfId)}
              title={origStats ? 'Analyze this frame and apply a balanced auto correction' : 'Waiting for the preview to render'}
              onClick={() => setActiveGrade(autoGrade(origStats, activeGrade, 'all'))}
            >
              ✨ Auto enhance
            </button>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!origStats || (mode === 'keyframes' && !selectedKfId)} onClick={() => setActiveGrade(autoGrade(origStats, activeGrade, 'light'))}>Auto light</button>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!origStats || (mode === 'keyframes' && !selectedKfId)} onClick={() => setActiveGrade(autoGrade(origStats, activeGrade, 'color'))}>Auto color</button>
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              disabled={mode === 'keyframes' && !selectedKfId}
              title="Clear ALL grading back to the untouched original (keeps only the camera de-log for RED/log footage)"
              onClick={() => setActiveGrade(neutralLook(activeGrade))}
            >
              Reset
            </button>
          </div>
          <div className="cs-autobar-label">Styles</div>
          <div className="cs-autobar-styles">
            {PRESETS.map((p) => {
              // "None" = a TRUE reset to the ungraded original (decode-only for
              // log footage) — replacing the whole grade with the default look
              // left a residual S-curve baked in on de-logged clips.
              const isNone = p.id === 'none';
              // applyLook stacks display looks on top of a camera-log decode
              // (instead of replacing it), so presets don't wash out RED clips.
              const chipGrade = isNone ? neutralLook(activeGrade) : applyLook(activeGrade, p.grade);
              return (
                <button
                  key={p.id}
                  className={`cs-stylechip ${gradeSignature(activeGrade) === gradeSignature(chipGrade) ? 'on' : ''}`}
                  disabled={mode === 'keyframes' && !selectedKfId}
                  title={isNone ? 'Remove the look entirely — back to the ungraded original' : `Apply the ${p.name} look`}
                  onClick={() => setActiveGrade(isNone ? chipGrade : stampPreset(chipGrade, p))}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
          <div className="cs-help" style={{ marginTop: 6 }}>
            {mode === 'keyframes'
              ? 'Auto / Styles apply to the selected keyframe. Every edit is non-destructive.'
              : 'Auto analyzes the current frame (exposure, contrast, white balance). Styles are themed looks — start from one, then fine-tune below. Every edit is non-destructive.'}
          </div>
        </div>

        <div>
          <div className="cs-scopes-label">Scopes (graded)</div>
          <Scopes stats={stats} />
        </div>

        <GradePanel
          grade={activeGrade}
          onChange={setActiveGrade}
          kind="video"
          isRed={isRed}
          presets={presets || []}
          onSavePreset={(name, g) => addGradePreset({ name, grade: g, kind: 'video' })}
          onDeletePreset={(pid) => deleteGradePreset(pid)}
        />
      </div>
    </div>
  );
}
